import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { csAdminApi } from '../../services/api';
import { mapCompanySecretaryFromApi, normalizeSecretaryAssignedCompanies } from '../../utils/apiMappers';

const ManageSecretaryCompaniesModal = ({ open, onClose, secretary, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (!open || !secretary) return undefined;

    const assignedIds = (secretary.assignedCompanies || []).map((company) =>
      typeof company === 'object' ? company.id : company
    );

    const loadCompanies = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await csAdminApi.getCompanies();
        const list = (response.data?.data || []).map((company) => ({
          id: company.id || company.company_id,
          name: company.name || company.company_name,
        }));
        setCompanies(list);
        setSelected(list.filter((company) => assignedIds.includes(company.id)));
      } catch (err) {
        console.error('Failed to load companies:', err);
        setError('Failed to load companies. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadCompanies();
    return undefined;
  }, [open, secretary]);

  const handleSave = async () => {
    if (!secretary) return;

    setSaving(true);
    setError('');

    try {
      const companyIds = selected.map((company) => company.id);
      const response = await csAdminApi.updateCompanySecretaryAssignments(secretary.id, companyIds);
      const updated = mapCompanySecretaryFromApi(response.data?.data);
      if (!updated) {
        throw new Error('Invalid response from server');
      }

      const assignedCompanies = normalizeSecretaryAssignedCompanies(updated);
      onSaved({
        ...secretary,
        ...updated,
        assignedCompanies,
        statistics: {
          ...(secretary.statistics || {}),
          totalCompanies: assignedCompanies.length,
        },
      });
      onClose();
    } catch (err) {
      console.error('Failed to save company assignments:', err);
      const message = err.response?.data?.error?.message || 'Failed to save company assignments.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6">Manage Company Assignments</Typography>
          {secretary?.name && (
            <Typography variant="body2" color="text.secondary">
              {secretary.name}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Autocomplete
            multiple
            options={companies}
            getOptionLabel={(option) => option.name}
            value={selected}
            onChange={(event, newValue) => setSelected(newValue)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Assigned Companies"
                placeholder="Select companies"
                helperText="Choose which companies this secretary can manage"
              />
            )}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={loading || saving}>
          {saving ? 'Saving...' : 'Save Assignments'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageSecretaryCompaniesModal;
